import React from 'react';
import AnnualOperationPlanTableColGroup from '../annual/AnnualOperationPlanTableColGroup';
import { getOperationPlanFooterRowClassName } from '../../utils/annualOperationPlanTableHelpers';
import type { AnnualOperationPlanDocumentPayload } from '../../utils/documentExportPreviewStorage';
import type { AnnualOperationPlanExportRow } from '../../utils/annualOperationPlanExcelExport';

export type AnnualOperationPlanExportDocumentProps = {
  payload: AnnualOperationPlanDocumentPayload;
};

function renderWeekRow(row: AnnualOperationPlanExportRow, rowKey: string) {
  return (
    <tr key={rowKey}>
      <td>{row.monthLabel}</td>
      <td>{row.weekIndex}</td>
      <td className="cp-annual-operation-plan-count-cell">{row.instructionalDayCount}</td>
      {row.weekdayCells.map((day, dayIdx) => (
        <td
          key={`operation-day-${rowKey}-${dayIdx}`}
          className={[
            'cp-annual-operation-plan-day-cell',
            !day.inTerm ? 'cp-annual-operation-plan-day-inactive' : '',
            day.dayKind === 'holiday' ? 'cp-weekly-cell-holiday' : '',
            day.dayKind === 'closure' || day.dayKind === 'vacation' ? 'cp-annual-operation-plan-day-closure' : '',
            day.hasFixedEvents ? 'cp-annual-operation-plan-day-fixed' : '',
            day.fixedDayGeneralMemoOnly ? 'cp-annual-operation-plan-day-memo-general' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={
            day.hasFixedEvents && !day.fixedDayGeneralMemoOnly && day.fixedDayTextColor
              ? { color: day.fixedDayTextColor }
              : undefined
          }
        >
          {day.value}
        </td>
      ))}
      {row.classHourCells.map((classHour, hourIdx) => (
        <td
          key={`operation-hours-${rowKey}-${hourIdx}`}
          className={[
            'cp-annual-operation-plan-hours-cell',
            classHour.adjusted ? 'cp-annual-operation-plan-hours-cell-adjusted' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {classHour.value}
        </td>
      ))}
      <td className="cp-annual-operation-plan-activity-cell">
        {row.activityLines.map((line, idx) => (
          <React.Fragment key={`activity-${rowKey}-${idx}`}>
            <span
              className={[
                'cp-annual-operation-plan-activity-line',
                line.kind === 'calendar' ? 'cp-annual-operation-plan-activity-daykind' : '',
                line.kind === 'fixed' ? 'cp-annual-operation-plan-activity-fixed' : '',
                line.kind === 'shortday' ? 'cp-annual-operation-plan-activity-shortday' : '',
                line.isGeneralMemo ? 'cp-annual-operation-plan-activity-memo-general' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={
                line.kind === 'fixed' && line.colorHex && !line.isGeneralMemo ? { color: line.colorHex } : undefined
              }
            >
              <span className="cp-annual-operation-plan-activity-datetime">{line.datePart}</span>
              <span className="cp-annual-operation-plan-activity-tags">
                {line.tagCommon ? (
                  <span className="cp-annual-operation-plan-activity-tag cp-annual-operation-plan-activity-tag--common">
                    공통
                  </span>
                ) : (
                  line.gradeNums.map((g) => (
                    <span key={`${rowKey}-${idx}-g${g}`} className="cp-annual-operation-plan-activity-tag">
                      {g}
                    </span>
                  ))
                )}
              </span>
              <span className="cp-annual-operation-plan-activity-name">{line.name}</span>
            </span>
            {idx < row.activityLines.length - 1 ? <br /> : null}
          </React.Fragment>
        ))}
      </td>
      <td className="cp-annual-operation-plan-remark-cell">
        {row.remarkTexts.map((text, idx) => (
          <div key={`op-wk-rmk-${rowKey}-${idx}`} className="cp-annual-operation-plan-remark-week-row">
            <span className="cp-annual-week-memo-week-mark" aria-hidden>
              주
            </span>
            <span className="cp-annual-operation-plan-remark-week-text">{text}</span>
          </div>
        ))}
      </td>
    </tr>
  );
}

function renderFooterRow(
  footer: {
    kind: 'subtotal' | 'grandTotal';
    label: string;
    termNumber?: 1 | 2;
    totalInstructionalDays: number;
    totalHoursByColumnKey: Record<string, number | string>;
    vacationText?: string;
  },
  classHeaders: AnnualOperationPlanDocumentPayload['classHeaders'],
  weekdayCount: number
) {
  return (
    <tr
      key={`operation-plan-footer-${footer.kind}-${footer.label}`}
      className={getOperationPlanFooterRowClassName(footer)}
    >
      <td colSpan={2}>{footer.label}</td>
      <td className="cp-annual-operation-plan-count-cell">{footer.totalInstructionalDays}</td>
      <td colSpan={weekdayCount} className="cp-annual-operation-plan-total-hours-label">
        수업 시수
      </td>
      {classHeaders.map((header) => (
        <td key={`operation-total-hours-${header.columnKey}`} className="cp-annual-operation-plan-hours-cell">
          {header.gradeId === 'none' ? '-' : (footer.totalHoursByColumnKey[header.columnKey] ?? 0)}
        </td>
      ))}
      <td className="cp-annual-operation-plan-activity-cell">{footer.vacationText ?? ''}</td>
      <td className="cp-annual-operation-plan-remark-cell"></td>
    </tr>
  );
}

const AnnualOperationPlanExportDocument = React.forwardRef<HTMLDivElement, AnnualOperationPlanExportDocumentProps>(
  function AnnualOperationPlanExportDocument({ payload }, ref) {
    const {
      title,
      metaSubtitle,
      scopeClassTitle,
      scopeGradeNumbers,
      weekdays,
      classHeaders,
      rows,
      totalInstructionalDays,
      totalHoursByColumnKey,
      vacationText,
      footerRows,
      tableBodyItems,
    } = payload;

    const bodyItems =
      tableBodyItems && tableBodyItems.length > 0
        ? tableBodyItems
        : [
            ...rows
              .filter((row) => !row.rowKind || row.rowKind === 'week')
              .map((row, index) => ({ kind: 'week' as const, row, key: `week-${index}` })),
            {
              kind: 'footer' as const,
              footer:
                footerRows && footerRows.length > 0
                  ? footerRows[footerRows.length - 1]!
                  : {
                      kind: 'grandTotal' as const,
                      label: '총계',
                      totalInstructionalDays: Number(totalInstructionalDays),
                      totalHoursByColumnKey: Object.fromEntries(
                        Object.entries(totalHoursByColumnKey).map(([key, value]) => [key, Number(value)])
                      ),
                      vacationText,
                    },
            },
          ];

    return (
      <div ref={ref} className="cp-document-export-preview-sheet cp-annual-operation-plan-export-root">
        <div className="cp-annual-operation-plan-export-meta" data-pdf-element="header">
          <p className="cp-annual-operation-plan-export-title">{title}</p>
          <div className="cp-annual-operation-plan-intro">
            {scopeClassTitle ? (
              <div className="cp-annual-operation-plan-scope-class">
                <div className="cp-grade-track-class-block" aria-label="대상 클래스">
                  <span className="cp-grade-track-class-block-label">{scopeClassTitle}</span>
                  {(scopeGradeNumbers ?? []).map((num) => (
                    <span key={num} className="class-grade-chip-display">
                      {num}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            <p className="cp-annual-operation-plan-subtitle">{metaSubtitle}</p>
          </div>
        </div>
        <div className="cp-annual-operation-plan-table-wrap">
          <table className="cp-annual-operation-plan-table">
            <AnnualOperationPlanTableColGroup
              weekdayCount={weekdays.length}
              classHeaderCount={classHeaders.length}
            />
            <thead>
              <tr>
                <th rowSpan={2}>월</th>
                <th rowSpan={2}>주</th>
                <th rowSpan={2}>수업일수</th>
                <th colSpan={5}>연간 수업 일수</th>
                <th colSpan={Math.max(1, classHeaders.length)}>클래스별 수업시수</th>
                <th rowSpan={2}>주요 교육 활동</th>
                <th rowSpan={2}>교육주간 및 비고</th>
              </tr>
              <tr>
                {weekdays.map((weekday) => (
                  <th key={`weekday-head-${weekday.label}`}>{weekday.label}</th>
                ))}
                {classHeaders.map((entry) => (
                  <th key={`class-head-${entry.columnKey}`} className="cp-annual-operation-plan-class-head">
                    <span className="cp-annual-operation-plan-class-head-wrap">
                      {entry.gradeId === 'none' ? (
                        <span className="cp-annual-operation-plan-class-head-empty">-</span>
                      ) : entry.gradeNumber ? (
                        <span className="cp-annual-operation-plan-grade-badge">{entry.gradeNumber}</span>
                      ) : null}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyItems.map((item, index) =>
                item.kind === 'week'
                  ? renderWeekRow(item.row, `operation-plan-${index}-${item.row.weekIndex}`)
                  : renderFooterRow(item.footer, classHeaders, weekdays.length)
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

export default AnnualOperationPlanExportDocument;
